import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Shield, AlertTriangle, RefreshCw, Search, Download, Unlock,
  Globe, Activity, Lock, Clock, KeyRound, Loader2, Ban, ShieldOff,
  CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import {
  adminSecurityFailedAccess, adminSecurityStats, adminSecurityLockouts,
  adminSecurityReleaseLockout, adminSecurityExportCsvUrl,
  adminSecurityListPermanentBlocks, adminSecurityCreatePermanentBlock,
  adminSecurityRemovePermanentBlock,
} from "../../lib/api";

const Card = ({ children, className = "", testid }) => (
  <div
    data-testid={testid}
    className={`border border-[#2D2A26] bg-[#0F0F0F] p-4 ${className}`}
  >
    {children}
  </div>
);

const StatTile = ({ label, value, tone = "default", testid }) => {
  const toneCls =
    tone === "danger" ? "text-[#D92D20]" :
    tone === "warn" ? "text-[#E0A526]" :
    tone === "ok" ? "text-[#5DD4B3]" :
    "text-[#E4DFD3]";
  return (
    <Card testid={testid} className="space-y-1">
      <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#757065]">
        {label}
      </div>
      <div className={`font-display text-3xl ${toneCls}`}>{value ?? "—"}</div>
    </Card>
  );
};

const ReasonBadge = ({ reason }) => {
  const tones = {
    no_session: "#757065",
    not_allowlisted: "#E0A526",
    bad_operator_token: "#D92D20",
    locked_out: "#D92D20",
    ip_rate_limited: "#E0A526",
    otp_mismatch: "#D92D20",
    otp_expired: "#757065",
    otp_no_active_code: "#757065",
    otp_bad_format: "#757065",
    totp_mismatch: "#D92D20",
    totp_bad_format: "#757065",
    totp_not_enabled: "#757065",
  };
  const color = tones[reason] || "#9FA8D4";
  return (
    <span
      style={{ color, borderColor: `${color}80` }}
      className="inline-flex items-center px-2 py-0.5 border font-mono text-[9px] uppercase tracking-[0.25em]"
    >
      {reason || "—"}
    </span>
  );
};

const relativeTime = (iso) => {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  const diff = Math.max(0, Date.now() - t);
  if (diff < 60_000) return `${Math.floor(diff / 1000)}s ago`;
  if (diff < 3600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return `${Math.floor(diff / 86_400_000)}d ago`;
};

const PAGE_SIZE = 25;

export const AdminSecurityFailedAccess = ({ opToken, userToken }) => {
  const [tab, setTab] = useState("log"); // "log" | "blocks"
  const [stats, setStats] = useState(null);
  const [attempts, setAttempts] = useState([]);
  const [lockouts, setLockouts] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [blocksTotal, setBlocksTotal] = useState(0);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState("");
  const [ipFilter, setIpFilter] = useState("");
  const [countryFilter, setCountryFilter] = useState("");
  const [fpFilter, setFpFilter] = useState("");
  const [reasonFilter, setReasonFilter] = useState("");
  const [autoRefresh, setAutoRefresh] = useState(false);

  const token = opToken;

  const refreshBlocks = useCallback(async () => {
    try {
      const b = await adminSecurityListPermanentBlocks(token, { limit: 500 }, userToken);
      setBlocks(b.blocks || []);
      setBlocksTotal(b.total || 0);
    } catch {
      // owner-only — fail silently for non-owner admins
    }
  }, [token, userToken]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [s, a, l] = await Promise.all([
        adminSecurityStats(token, userToken),
        adminSecurityFailedAccess(token, {
          limit: PAGE_SIZE,
          skip,
          ...(search ? { search } : {}),
          ...(ipFilter ? { ip: ipFilter } : {}),
          ...(countryFilter ? { country: countryFilter } : {}),
          ...(fpFilter ? { fingerprint: fpFilter } : {}),
          ...(reasonFilter ? { reason: reasonFilter } : {}),
        }, userToken),
        adminSecurityLockouts(token, false, userToken),
      ]);
      setStats(s);
      setAttempts(a.attempts || []);
      setTotal(a.total || 0);
      setLockouts(l.lockouts || []);
      await refreshBlocks();
    } catch (e) {
      const code = e?.response?.status;
      if (code === 403) setError("Owner-only — your role can't view security logs.");
      else if (code === 401) setError("Session expired. Please sign in again.");
      else setError("Failed to load security data.");
    } finally {
      setLoading(false);
    }
  }, [token, userToken, skip, search, ipFilter, countryFilter, fpFilter, reasonFilter, refreshBlocks]);

  useEffect(() => { loadAll(); }, [loadAll]);

  useEffect(() => {
    if (!autoRefresh) return;
    const t = setInterval(loadAll, 10_000);
    return () => clearInterval(t);
  }, [autoRefresh, loadAll]);

  const onSearchSubmit = (e) => {
    e.preventDefault();
    setSkip(0);
    loadAll();
  };

  const onClearFilters = () => {
    setSearch("");
    setIpFilter("");
    setCountryFilter("");
    setFpFilter("");
    setReasonFilter("");
    setSkip(0);
  };

  const onReleaseLockout = async (fp) => {
    if (!window.confirm("Release this lockout? The attacker will be able to retry immediately.")) {
      return;
    }
    try {
      await adminSecurityReleaseLockout(token, fp, userToken);
      toast.success("Lockout released.");
      loadAll();
    } catch {
      toast.error("Failed to release lockout.");
    }
  };

  // Permanent-block sets for quick lookup
  const blockedIps = useMemo(
    () => new Set(blocks.filter((b) => b.kind === "ip").map((b) => b.value)),
    [blocks],
  );
  const blockedFps = useMemo(
    () => new Set(blocks.filter((b) => b.kind === "fingerprint").map((b) => b.value)),
    [blocks],
  );

  const onBlockForever = async (kind, value, hint = "") => {
    const label = kind === "ip" ? "IP" : "fingerprint";
    const reason = window.prompt(
      `Block ${label} ${kind === "ip" ? value : value.slice(0, 16) + "…"} FOREVER.\n\n` +
      `Reason (visible to Owner only):`,
      hint || "",
    );
    if (reason === null) return;          // cancelled
    try {
      await adminSecurityCreatePermanentBlock(
        token, { kind, value, reason }, userToken,
      );
      toast.success(`${label} permanently blocked.`);
      loadAll();
    } catch (e) {
      const code = e?.response?.status;
      if (code === 401 || code === 403) toast.error("Owner-only action.");
      else toast.error(`Failed to block ${label}.`);
    }
  };

  const onUnblock = async (kind, value) => {
    const label = kind === "ip" ? "IP" : "fingerprint";
    if (!window.confirm(`Unblock this ${label}? Future requests from it will follow the normal lockout ladder.`)) return;
    try {
      await adminSecurityRemovePermanentBlock(token, kind, value, userToken);
      toast.success(`${label} unblocked.`);
      loadAll();
    } catch (e) {
      const code = e?.response?.status;
      if (code === 401 || code === 403) toast.error("Owner-only action.");
      else toast.error(`Failed to unblock ${label}.`);
    }
  };

  const csvUrl = useMemo(() => adminSecurityExportCsvUrl(token), [token]);

  return (
    <div className="space-y-6" data-testid="admin-security-failed-access">
      {/* Header */}
      <div className="border-l-2 border-[#D92D20] pl-4">
        <div className="flex items-center gap-2 mb-1">
          <Shield size={14} className="text-[#D92D20]" />
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20]">
            // SECURITY · FAILED ACCESS
          </span>
        </div>
        <h1 className="font-display text-3xl text-[#E4DFD3]">
          Admin Authentication Activity
        </h1>
        <p className="font-mono text-[11px] text-[#757065] mt-1">
          Every failed admin login is silently logged here. Patterns of attack,
          coordinated probes, and active lockouts are surfaced for the Owner only.
        </p>
      </div>

      {/* Tab bar */}
      <div className="flex items-center gap-1 border-b border-[#2D2A26]">
        <button
          data-testid="sec-tab-log"
          onClick={() => setTab("log")}
          className={`px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em] border-b-2 -mb-px ${
            tab === "log"
              ? "border-[#D92D20] text-[#E4DFD3]"
              : "border-transparent text-[#757065] hover:text-[#A39E93]"
          }`}
        >
          Activity Log
        </button>
        <button
          data-testid="sec-tab-blocks"
          onClick={() => setTab("blocks")}
          className={`px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em] border-b-2 -mb-px flex items-center gap-1.5 ${
            tab === "blocks"
              ? "border-[#D92D20] text-[#E4DFD3]"
              : "border-transparent text-[#757065] hover:text-[#A39E93]"
          }`}
        >
          <Ban size={11} />
          Permanent Blocks
          {blocksTotal > 0 && (
            <span className="px-1.5 py-0.5 border border-[#D92D20] text-[#D92D20] text-[9px]">
              {blocksTotal}
            </span>
          )}
        </button>
      </div>

      {error && (
        <Card className="border-[#D92D20]/50">
          <div className="flex items-center gap-2 text-[#D92D20] font-mono text-xs">
            <AlertTriangle size={12} /> {error}
          </div>
        </Card>
      )}

      {tab === "log" && (
        <>
      {/* Stats Tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <StatTile
          testid="sec-stat-total"
          label="Total Failures"
          value={stats?.total ?? "—"}
        />
        <StatTile
          testid="sec-stat-24h"
          label="Last 24 Hours"
          value={stats?.last_24h ?? "—"}
          tone={stats?.last_24h > 0 ? "warn" : "default"}
        />
        <StatTile
          testid="sec-stat-1h"
          label="Last Hour"
          value={stats?.last_1h ?? "—"}
          tone={stats?.last_1h > 0 ? "danger" : "default"}
        />
        <StatTile
          testid="sec-stat-active-lockouts"
          label="Active Lockouts"
          value={stats?.active_lockouts ?? "—"}
          tone={stats?.active_lockouts > 0 ? "danger" : "ok"}
        />
        <StatTile
          testid="sec-stat-expired-lockouts"
          label="Expired Lockouts (24h)"
          value={stats?.expired_lockouts ?? "—"}
        />
      </div>

      {/* Controls */}
      <Card>
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <button
            data-testid="sec-refresh"
            onClick={loadAll}
            className="inline-flex items-center gap-1.5 border border-[#454038] hover:border-[#D92D20] text-[#A39E93] hover:text-[#E4DFD3] px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.25em]"
          >
            <RefreshCw size={11} /> Refresh
          </button>
          <label className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39E93]">
            <input
              type="checkbox"
              data-testid="sec-auto-refresh"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="accent-[#D92D20]"
            />
            Auto-refresh (10s)
          </label>
          <a
            href={csvUrl}
            data-testid="sec-csv-export"
            className="inline-flex items-center gap-1.5 border border-[#454038] hover:border-[#E0A526] text-[#A39E93] hover:text-[#E0A526] px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.25em] ml-auto"
          >
            <Download size={11} /> Export CSV
          </a>
        </div>

        <form onSubmit={onSearchSubmit} className="grid grid-cols-1 md:grid-cols-5 gap-2">
          <div className="md:col-span-2 relative">
            <Search size={11} className="absolute left-2 top-1/2 -translate-y-1/2 text-[#757065]" />
            <input
              data-testid="sec-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search (IP, country, fingerprint, reason)…"
              className="w-full pl-7 pr-2 py-2 bg-[#0a0907] border border-[#2D2A26] focus:border-[#D92D20] text-[#E4DFD3] font-mono text-xs"
            />
          </div>
          <input
            data-testid="sec-filter-ip"
            value={ipFilter}
            onChange={(e) => setIpFilter(e.target.value)}
            placeholder="IP"
            className="px-2 py-2 bg-[#0a0907] border border-[#2D2A26] focus:border-[#D92D20] text-[#E4DFD3] font-mono text-xs"
          />
          <input
            data-testid="sec-filter-country"
            value={countryFilter}
            onChange={(e) => setCountryFilter(e.target.value)}
            placeholder="Country"
            className="px-2 py-2 bg-[#0a0907] border border-[#2D2A26] focus:border-[#D92D20] text-[#E4DFD3] font-mono text-xs"
          />
          <input
            data-testid="sec-filter-fp"
            value={fpFilter}
            onChange={(e) => setFpFilter(e.target.value)}
            placeholder="Fingerprint prefix"
            className="px-2 py-2 bg-[#0a0907] border border-[#2D2A26] focus:border-[#D92D20] text-[#E4DFD3] font-mono text-xs"
          />
          <button
            data-testid="sec-apply-filters"
            type="submit"
            className="border border-[#D92D20] text-[#D92D20] hover:bg-[#1a0808] px-3 py-2 font-mono text-[9px] uppercase tracking-[0.25em] md:col-span-2"
          >
            Apply filters
          </button>
          <button
            type="button"
            onClick={onClearFilters}
            className="border border-[#454038] text-[#A39E93] hover:border-[#D92D20] px-3 py-2 font-mono text-[9px] uppercase tracking-[0.25em] md:col-span-2"
          >
            Clear
          </button>
        </form>
      </Card>

      {error && (
        <Card className="border-[#D92D20]/50">
          <div className="flex items-center gap-2 text-[#D92D20] font-mono text-xs">
            <AlertTriangle size={12} /> {error}
          </div>
        </Card>
      )}

      {/* Active lockouts */}
      <Card testid="sec-lockouts-card">
        <div className="flex items-center gap-2 mb-3">
          <Lock size={12} className="text-[#D92D20]" />
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20]">
            Current & Recent Lockouts
          </span>
        </div>
        {lockouts.length === 0 ? (
          <div className="font-mono text-[10px] text-[#757065] italic">
            No lockouts on record.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full font-mono text-[10px]">
              <thead>
                <tr className="text-[#757065] uppercase tracking-[0.25em] border-b border-[#2D2A26]">
                  <th className="text-left py-1.5">Fingerprint</th>
                  <th className="text-left">IP</th>
                  <th className="text-left">Country</th>
                  <th className="text-left">Tier</th>
                  <th className="text-left">Status</th>
                  <th className="text-left">Total</th>
                  <th className="text-left">Last seen</th>
                  <th className="text-left">Action</th>
                </tr>
              </thead>
              <tbody>
                {lockouts.map((row) => (
                  <tr
                    key={row.fingerprint}
                    className="border-b border-[#2D2A26]/50 text-[#E4DFD3]"
                    data-testid={`sec-lockout-row-${row.fingerprint.slice(0, 8)}`}
                  >
                    <td className="py-1.5 pr-2">
                      <span className="text-[#9FA8D4]">{(row.fingerprint || "").slice(0, 16)}…</span>
                    </td>
                    <td className="pr-2">{row.ip_address || "—"}</td>
                    <td className="pr-2">{row.country || "—"}</td>
                    <td className="pr-2">
                      <span className="px-1.5 border border-[#E0A526] text-[#E0A526]">
                        {row.offense_tier || 0}
                      </span>
                    </td>
                    <td className="pr-2">
                      {row.is_active ? (
                        <span className="inline-flex items-center gap-1 text-[#D92D20]">
                          <span className="w-1.5 h-1.5 bg-[#D92D20] rounded-full animate-pulse" />
                          ACTIVE
                        </span>
                      ) : (
                        <span className="text-[#757065]">expired</span>
                      )}
                    </td>
                    <td className="pr-2">{row.total_attempts || 0}</td>
                    <td className="pr-2 text-[#A39E93]">
                      <Clock size={9} className="inline mr-1 mb-0.5" />
                      {relativeTime(row.last_attempt_at)}
                    </td>
                    <td className="flex items-center gap-1 py-1.5">
                      {row.is_active && (
                        <button
                          data-testid={`sec-release-${row.fingerprint.slice(0, 8)}`}
                          onClick={() => onReleaseLockout(row.fingerprint)}
                          className="inline-flex items-center gap-1 border border-[#5DD4B3]/50 hover:border-[#5DD4B3] text-[#5DD4B3] hover:bg-[#0a1a14] px-1.5 py-0.5"
                          title="Release this lockout"
                        >
                          <Unlock size={9} /> Release
                        </button>
                      )}
                      {row.ip_address && !blockedIps.has(row.ip_address) && (
                        <button
                          data-testid={`sec-block-ip-${row.fingerprint.slice(0, 8)}`}
                          onClick={() => onBlockForever("ip", row.ip_address)}
                          className="inline-flex items-center gap-1 border border-[#D92D20]/50 hover:border-[#D92D20] text-[#D92D20] hover:bg-[#1a0808] px-1.5 py-0.5"
                          title="Permanently block this IP"
                        >
                          <Ban size={9} /> Block IP
                        </button>
                      )}
                      {row.fingerprint && !blockedFps.has(row.fingerprint) && (
                        <button
                          data-testid={`sec-block-fp-${row.fingerprint.slice(0, 8)}`}
                          onClick={() => onBlockForever("fingerprint", row.fingerprint)}
                          className="inline-flex items-center gap-1 border border-[#D92D20]/50 hover:border-[#D92D20] text-[#D92D20] hover:bg-[#1a0808] px-1.5 py-0.5"
                          title="Permanently block this fingerprint"
                        >
                          <Ban size={9} /> Block FP
                        </button>
                      )}
                      {(row.ip_permanently_blocked || row.fingerprint_permanently_blocked) && (
                        <span
                          className="inline-flex items-center gap-1 border border-[#D92D20] text-[#D92D20] px-1.5 py-0.5"
                          title="Permanently blocked"
                        >
                          <Ban size={9} /> BLOCKED
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Attempts table */}
      <Card testid="sec-attempts-card">
        <div className="flex items-center gap-2 mb-3">
          <Activity size={12} className="text-[#D92D20]" />
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20]">
            Failed Access Log
          </span>
          <span className="font-mono text-[10px] text-[#757065] ml-auto">
            {total} total
          </span>
        </div>

        {loading ? (
          <div className="py-12 flex justify-center">
            <Loader2 size={20} className="animate-spin text-[#D92D20]" />
          </div>
        ) : attempts.length === 0 ? (
          <div className="font-mono text-[10px] text-[#757065] italic py-6 text-center">
            // No failed attempts found.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full font-mono text-[10px]">
              <thead>
                <tr className="text-[#757065] uppercase tracking-[0.25em] border-b border-[#2D2A26]">
                  <th className="text-left py-1.5">Time</th>
                  <th className="text-left">IP</th>
                  <th className="text-left">Country / City</th>
                  <th className="text-left">Fingerprint</th>
                  <th className="text-left">Reason</th>
                  <th className="text-left">Path</th>
                  <th className="text-left">Action</th>
                </tr>
              </thead>
              <tbody>
                {attempts.map((row, i) => (
                  <tr
                    key={`${row.timestamp}-${i}`}
                    className="border-b border-[#2D2A26]/50 text-[#E4DFD3] hover:bg-[#0a0907]"
                    data-testid={`sec-attempt-row-${i}`}
                  >
                    <td className="py-1.5 pr-2 text-[#A39E93]">
                      {relativeTime(row.timestamp)}
                    </td>
                    <td className="pr-2">
                      {row.ip_address || "—"}
                      {row.ip_permanently_blocked && (
                        <Ban size={9} className="inline ml-1 text-[#D92D20]" />
                      )}
                    </td>
                    <td className="pr-2 text-[#A39E93]">
                      <Globe size={9} className="inline mr-1 mb-0.5" />
                      {row.country || "—"}{row.city ? ` / ${row.city}` : ""}
                    </td>
                    <td className="pr-2">
                      <button
                        onClick={() => {
                          setFpFilter(row.browser_fingerprint);
                          setSkip(0);
                        }}
                        className="text-[#9FA8D4] hover:text-[#D92D20]"
                        title="Filter by this fingerprint"
                      >
                        {(row.browser_fingerprint || "").slice(0, 12)}…
                      </button>
                      {row.fingerprint_permanently_blocked && (
                        <Ban size={9} className="inline ml-1 text-[#D92D20]" />
                      )}
                    </td>
                    <td className="pr-2"><ReasonBadge reason={row.failure_reason} /></td>
                    <td className="pr-2 text-[#757065]">{row.request_path || "—"}</td>
                    <td className="pr-2">
                      <div className="flex flex-col gap-1">
                        {row.ip_address && !row.ip_permanently_blocked && (
                          <button
                            data-testid={`sec-attempt-block-ip-${i}`}
                            onClick={() => onBlockForever("ip", row.ip_address)}
                            className="inline-flex items-center gap-1 border border-[#D92D20]/40 hover:border-[#D92D20] text-[#D92D20] px-1.5 py-0.5 text-[9px]"
                            title="Block IP forever"
                          >
                            <Ban size={9} /> IP
                          </button>
                        )}
                        {row.browser_fingerprint && !row.fingerprint_permanently_blocked && (
                          <button
                            data-testid={`sec-attempt-block-fp-${i}`}
                            onClick={() => onBlockForever("fingerprint", row.browser_fingerprint)}
                            className="inline-flex items-center gap-1 border border-[#D92D20]/40 hover:border-[#D92D20] text-[#D92D20] px-1.5 py-0.5 text-[9px]"
                            title="Block fingerprint forever"
                          >
                            <Ban size={9} /> FP
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        <div className="flex items-center justify-between pt-3 mt-3 border-t border-[#2D2A26] font-mono text-[10px] text-[#A39E93]">
          <div>
            Showing {skip + 1}–{Math.min(skip + attempts.length, total)} of {total}
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="sec-page-prev"
              disabled={skip === 0}
              onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))}
              className="border border-[#454038] hover:border-[#D92D20] text-[#A39E93] hover:text-[#E4DFD3] px-2 py-1 uppercase tracking-[0.25em] disabled:opacity-30 disabled:cursor-not-allowed"
            >
              Prev
            </button>
            <button
              data-testid="sec-page-next"
              disabled={skip + PAGE_SIZE >= total}
              onClick={() => setSkip(skip + PAGE_SIZE)}
              className="border border-[#454038] hover:border-[#D92D20] text-[#A39E93] hover:text-[#E4DFD3] px-2 py-1 uppercase tracking-[0.25em] disabled:opacity-30 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      </Card>

      {/* Aggregations */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Card testid="sec-by-country">
          <div className="flex items-center gap-2 mb-3">
            <Globe size={11} className="text-[#D92D20]" />
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20]">
              By Country · last 24h
            </span>
          </div>
          {stats?.by_country?.length ? (
            <ul className="space-y-1">
              {stats.by_country.map((row) => (
                <li
                  key={row.country || "_unk"}
                  className="flex items-center justify-between font-mono text-[10px] text-[#E4DFD3]"
                >
                  <span>{row.country || "Unknown"}</span>
                  <span className="text-[#D92D20]">{row.count}</span>
                </li>
              ))}
            </ul>
          ) : (
            <div className="font-mono text-[10px] text-[#757065] italic">No data.</div>
          )}
        </Card>

        <Card testid="sec-by-reason">
          <div className="flex items-center gap-2 mb-3">
            <KeyRound size={11} className="text-[#D92D20]" />
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20]">
              By Failure Reason · last 24h
            </span>
          </div>
          {stats?.by_reason?.length ? (
            <ul className="space-y-1">
              {stats.by_reason.map((row) => (
                <li
                  key={row.reason || "_unk"}
                  className="flex items-center justify-between font-mono text-[10px] text-[#E4DFD3]"
                >
                  <ReasonBadge reason={row.reason} />
                  <span className="text-[#D92D20]">{row.count}</span>
                </li>
              ))}
            </ul>
          ) : (
            <div className="font-mono text-[10px] text-[#757065] italic">No data.</div>
          )}
        </Card>
      </div>
        </>
      )}

      {tab === "blocks" && (
        <PermanentBlocksView
          blocks={blocks}
          loading={loading}
          onUnblock={onUnblock}
          onAddBlock={onBlockForever}
        />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Permanent Blocks Tab
// ---------------------------------------------------------------------------

const PermanentBlocksView = ({ blocks, loading, onUnblock, onAddBlock }) => {
  const [filter, setFilter] = useState("all"); // all | ip | fingerprint
  const [newKind, setNewKind] = useState("ip");
  const [newValue, setNewValue] = useState("");
  const [newReason, setNewReason] = useState("");

  const visible = useMemo(() => {
    if (filter === "all") return blocks;
    return blocks.filter((b) => b.kind === filter);
  }, [blocks, filter]);

  const submitNew = async (e) => {
    e.preventDefault();
    if (!newValue.trim()) {
      toast.error("Enter an IP or fingerprint to block.");
      return;
    }
    await onAddBlock(newKind, newValue.trim(), newReason || "");
    setNewValue("");
    setNewReason("");
  };

  return (
    <div className="space-y-4" data-testid="sec-perm-blocks-view">
      <Card>
        <div className="flex items-center gap-2 mb-3">
          <ShieldOff size={12} className="text-[#D92D20]" />
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20]">
            Permanent Block List
          </span>
          <span className="font-mono text-[10px] text-[#757065] ml-auto">
            {blocks.length} active
          </span>
        </div>
        <p className="font-mono text-[11px] text-[#A39E93] leading-relaxed">
          IPs and browser-fingerprints on this list are blocked indefinitely.
          Every request from them receives the same{" "}
          <span className="text-[#E4DFD3]">ACCESS DENIED</span> response as any
          other auth failure — they never learn they&apos;re blocked. Only the Owner
          may add or remove entries.
        </p>
      </Card>

      <Card>
        <div className="flex items-center gap-2 mb-3">
          <Ban size={12} className="text-[#D92D20]" />
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20]">
            Add a Permanent Block
          </span>
        </div>
        <form onSubmit={submitNew} className="grid grid-cols-1 md:grid-cols-12 gap-2">
          <select
            data-testid="sec-add-block-kind"
            value={newKind}
            onChange={(e) => setNewKind(e.target.value)}
            className="md:col-span-2 px-2 py-2 bg-[#0a0907] border border-[#2D2A26] focus:border-[#D92D20] text-[#E4DFD3] font-mono text-xs"
          >
            <option value="ip">IP</option>
            <option value="fingerprint">Fingerprint</option>
          </select>
          <input
            data-testid="sec-add-block-value"
            value={newValue}
            onChange={(e) => setNewValue(e.target.value)}
            placeholder={newKind === "ip" ? "e.g. 198.51.100.42" : "64-char fingerprint hash"}
            className="md:col-span-4 px-2 py-2 bg-[#0a0907] border border-[#2D2A26] focus:border-[#D92D20] text-[#E4DFD3] font-mono text-xs"
          />
          <input
            data-testid="sec-add-block-reason"
            value={newReason}
            onChange={(e) => setNewReason(e.target.value)}
            placeholder="Reason (Owner-only)"
            className="md:col-span-4 px-2 py-2 bg-[#0a0907] border border-[#2D2A26] focus:border-[#D92D20] text-[#E4DFD3] font-mono text-xs"
          />
          <button
            data-testid="sec-add-block-submit"
            type="submit"
            className="md:col-span-2 inline-flex items-center justify-center gap-1.5 border border-[#D92D20] text-[#D92D20] hover:bg-[#1a0808] px-3 py-2 font-mono text-[9px] uppercase tracking-[0.25em]"
          >
            <Ban size={11} /> Block forever
          </button>
        </form>
      </Card>

      <Card>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Lock size={12} className="text-[#D92D20]" />
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20]">
              Current Permanent Blocks
            </span>
          </div>
          <div className="flex items-center gap-1 font-mono text-[9px]">
            {["all", "ip", "fingerprint"].map((k) => (
              <button
                key={k}
                data-testid={`sec-blocks-filter-${k}`}
                onClick={() => setFilter(k)}
                className={`px-2 py-1 uppercase tracking-[0.25em] border ${
                  filter === k
                    ? "border-[#D92D20] text-[#D92D20]"
                    : "border-[#2D2A26] text-[#757065] hover:text-[#E4DFD3]"
                }`}
              >
                {k}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="py-12 flex justify-center">
            <Loader2 size={20} className="animate-spin text-[#D92D20]" />
          </div>
        ) : visible.length === 0 ? (
          <div className="font-mono text-[10px] text-[#757065] italic py-6 text-center">
            <CheckCircle2 size={14} className="inline mb-0.5 mr-1 text-[#5DD4B3]" />
            // No permanent blocks. The progressive lockout ladder is the only
            line of defense right now.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full font-mono text-[10px]">
              <thead>
                <tr className="text-[#757065] uppercase tracking-[0.25em] border-b border-[#2D2A26]">
                  <th className="text-left py-1.5">Kind</th>
                  <th className="text-left">Value</th>
                  <th className="text-left">Reason</th>
                  <th className="text-left">Blocked at</th>
                  <th className="text-left">By</th>
                  <th className="text-left">Action</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((row, i) => (
                  <tr
                    key={`${row.kind}-${row.value}`}
                    data-testid={`sec-perm-block-row-${i}`}
                    className="border-b border-[#2D2A26]/50 text-[#E4DFD3]"
                  >
                    <td className="py-1.5 pr-2">
                      <span className="inline-flex items-center px-1.5 py-0.5 border border-[#D92D20] text-[#D92D20] uppercase tracking-[0.25em]">
                        {row.kind}
                      </span>
                    </td>
                    <td className="pr-2 font-mono">
                      {row.kind === "ip" ? row.value
                        : (row.value || "").slice(0, 16) + "…"}
                    </td>
                    <td className="pr-2 text-[#A39E93]">{row.reason || "—"}</td>
                    <td className="pr-2 text-[#A39E93]">
                      <Clock size={9} className="inline mr-1 mb-0.5" />
                      {relativeTime(row.blocked_at)}
                    </td>
                    <td className="pr-2 text-[#757065]">{row.blocked_by || "—"}</td>
                    <td className="pr-2">
                      <button
                        data-testid={`sec-unblock-${i}`}
                        onClick={() => onUnblock(row.kind, row.value)}
                        className="inline-flex items-center gap-1 border border-[#5DD4B3]/50 hover:border-[#5DD4B3] text-[#5DD4B3] hover:bg-[#0a1a14] px-1.5 py-0.5"
                      >
                        <Unlock size={9} /> Unblock
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};

export default AdminSecurityFailedAccess;
